// components/CriarNotificacao.tsx
import React, { useState, useEffect } from "react";
import {
  Button,
  CircularProgress,
  Box,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  //  Grid, // REMOVIDO: Eliminando o componente Grid
  Chip,
  Autocomplete,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { API_URLS } from "../../config/apiUrls";

// Interface para o tipo de usuário retornado pela API (inalterada)
interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  ufId: number;
  cityId: string;
  acceptedTerms: boolean;
  planExpiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  city: {
    id: string;
    name: string;
  } | null;
}

interface CriarNotificacaoProps {
  token: string;
  role: "MASTER" | "PARTNER" | "ANALYST";
  onNotificationCreated?: () => void;
}

const CriarNotificacao: React.FC<CriarNotificacaoProps> = ({
  token,
  role,
  onNotificationCreated,
}) => {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [title, setTitle] = useState<string>("");
  const [content, setContent] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [maxViews, setMaxViews] = useState<string | number>(0);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [isForAllUsers, setIsForAllUsers] = useState<boolean>(true);

  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [usersError, setUsersError] = useState<string | null>(null);

  const [loadingUpload, setLoadingUpload] = useState<boolean>(false);
  const [loadingCreate, setLoadingCreate] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("info");

  const handleOpenModal = () => {
    setOpenModal(true);
    setTitle("");
    setContent("");
    setStartDate("");
    setEndDate("");
    setMaxViews(0);
    setSelectedImage(null);
    setImageUrl("");
    setIsForAllUsers(true);
    setSelectedUsers([]);
    setSnackbarOpen(false);
    setUsersError(null);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    setUsersError(null);
    try {
      const response = await fetch(API_URLS.users, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Falha ao carregar os usuários.");
      }

      const data = await response.json();
      const validUsers = data.filter(
        (user: any) => user && user.id && user.name && user.email
      );
      setAllUsers(validUsers);
    } catch (err: any) {
      setUsersError(err.message || "Erro ao carregar usuários.");
      setSnackbarMessage(err.message || "Erro ao carregar usuários.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      console.error("Erro ao carregar usuários:", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (
      openModal &&
      !isForAllUsers &&
      allUsers.length === 0 &&
      !loadingUsers &&
      !usersError
    ) {
      fetchUsers();
    }
  }, [
    openModal,
    isForAllUsers,
    allUsers.length,
    loadingUsers,
    usersError,
    token,
  ]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedImage(event.target.files[0]);
      setImageUrl("");
    } else {
      setSelectedImage(null);
    }
  };

  const handleUploadImage = async () => {
    if (!selectedImage) {
      setSnackbarMessage("Por favor, selecione uma imagem para upload.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    setLoadingUpload(true);
    const formData = new FormData();
    formData.append("image", selectedImage);

    try {
      const response = await fetch(`${API_URLS.notifications}/upload-image`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Erro ao fazer upload da imagem.");
      }

      const data = await response.json();
      setImageUrl(data.imageUrl);
      setSnackbarMessage("Imagem enviada com sucesso!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err: any) {
      setSnackbarMessage(err.message || "Erro desconhecido no upload.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      console.error("Erro no upload da imagem:", err);
      setImageUrl("");
    } finally {
      setLoadingUpload(false);
    }
  };

  const handleCreateNotification = async () => {
    const finalMaxViews = Number(maxViews);

    if (!title || !content || !startDate || !endDate) {
      setSnackbarMessage("Por favor, preencha todos os campos obrigatórios.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    if (finalMaxViews <= 0) {
      setSnackbarMessage(
        "O 'Número Máximo de Visualizações' deve ser maior que zero."
      );
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    const startDateTime = new Date(startDate);
    const endDateTime = new Date(endDate);
    const currentDateTime = new Date();

    if (startDateTime >= endDateTime) {
      setSnackbarMessage(
        "A 'Data de Início' deve ser anterior à 'Data de Fim'."
      );
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    if (endDateTime <= currentDateTime) {
      setSnackbarMessage(
        "A 'Data de Fim' deve ser posterior à data e hora atuais."
      );
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    if (!isForAllUsers && selectedUsers.length === 0) {
      setSnackbarMessage(
        "Por favor, selecione pelo menos um usuário alvo para notificações específicas."
      );
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    setLoadingCreate(true);

    const notificationData: any = {
      title,
      content,
      startDate: startDateTime.toISOString(),
      endDate: endDateTime.toISOString(),
      maxViews: finalMaxViews,
      ...(imageUrl && { imageUrl }),
      isForAllUsers,
    };

    if (!isForAllUsers) {
      notificationData.targetUserIds = selectedUsers.map((user) => user.id);
    }

    try {
      const response = await fetch(API_URLS.notifications, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(notificationData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Erro ao criar notificação.");
      }

      setSnackbarMessage("Notificação criada com sucesso!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      handleCloseModal();
      if (onNotificationCreated) {
        onNotificationCreated();
      }
    } catch (err: any) {
      setSnackbarMessage(
        err.message || "Erro desconhecido ao criar notificação."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      console.error("Erro ao criar notificação:", err);
    } finally {
      setLoadingCreate(false);
    }
  };

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <Box>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddCircleOutlineIcon />}
        onClick={handleOpenModal}
        sx={{ mb: 3 }}
      >
        Criar Nova Notificação
      </Button>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Criar Nova Notificação</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              1. Upload da Imagem da Notificação (Opcional)
            </Typography>
            <Box sx={{ mb: 3 }}>
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="upload-image-button"
                type="file"
                onChange={handleImageChange}
              />
              <label htmlFor="upload-image-button">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  disabled={loadingUpload}
                >
                  {selectedImage ? selectedImage.name : "Selecionar Imagem"}
                </Button>
              </label>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUploadImage}
                disabled={!selectedImage || loadingUpload}
                sx={{ ml: 2 }}
              >
                {loadingUpload ? (
                  <CircularProgress size={24} />
                ) : (
                  "Fazer Upload"
                )}
              </Button>
              {imageUrl && (
                <Typography
                  variant="body2"
                  sx={{ mt: 1, color: "success.main" }}
                >
                  Imagem enviada:{" "}
                  <a href={imageUrl} target="_blank" rel="noopener noreferrer">
                    {imageUrl.split("/").pop()}
                  </a>
                  <Button
                    onClick={() => setImageUrl("")}
                    color="error"
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    Remover
                  </Button>
                </Typography>
              )}
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              2. Detalhes da Notificação
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Preencha os campos abaixo. A imagem é opcional.
            </Typography>

            <TextField
              label="Título da Notificação"
              fullWidth
              margin="normal"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <TextField
              label="Conteúdo da Notificação"
              fullWidth
              margin="normal"
              multiline
              rows={3}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              InputProps={{
                sx: {
                  "& .MuiInputBase-inputMultiline": {
                    resize: "vertical",
                  },
                },
              }}
            />

            {/* SUBSTITUINDO GRID CONTAINER E GRID ITEM POR BOX FLEXBOX */}
            <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
              {/* Campo Data de Início - Ocupa 50% em sm e 100% em xs */}
              <Box sx={{ width: { xs: "100%", sm: "50%" } }}>
                <TextField
                  label="Data de Início"
                  type="datetime-local"
                  fullWidth
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Box>
              {/* Campo Data de Fim - Ocupa 50% em sm e 100% em xs */}
              <Box sx={{ width: { xs: "100%", sm: "50%" } }}>
                <TextField
                  label="Data de Fim"
                  type="datetime-local"
                  fullWidth
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Box>
            </Box>

            <TextField
              label="Número Máximo de Visualizações por Usuário"
              type="number"
              fullWidth
              margin="normal"
              value={maxViews}
              onChange={(e) => {
                const value = e.target.value;
                setMaxViews(value === "" ? "" : Number(value));
              }}
              inputProps={{ min: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={isForAllUsers}
                  onChange={(e) => {
                    setIsForAllUsers(e.target.checked);
                    if (e.target.checked) {
                      setSelectedUsers([]);
                      setUsersError(null);
                    } else {
                      if (allUsers.length === 0 && !loadingUsers) {
                        fetchUsers();
                      }
                    }
                  }}
                  color="primary"
                />
              }
              label="Notificação para todos os usuários (Global)"
              sx={{ mt: 2, mb: 2 }}
            />

            {!isForAllUsers && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1" gutterBottom>
                  Selecione os Usuários Alvo
                </Typography>
                {loadingUsers ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", my: 2 }}
                  >
                    <CircularProgress size={24} />
                    <Typography sx={{ ml: 2 }}>
                      Carregando usuários...
                    </Typography>
                  </Box>
                ) : usersError ? (
                  <Alert severity="error">{usersError}</Alert>
                ) : (
                  <Autocomplete
                    multiple
                    id="select-target-users"
                    options={allUsers}
                    getOptionLabel={(option) => {
                      if (!option) return "";
                      const cityName = option.city ? option.city.name : "N/A";
                      return `${option.name || "Nome Desconhecido"} (${
                        option.email || "Email Desconhecido"
                      }) - ${cityName}`;
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    value={selectedUsers}
                    onChange={(event, newValue) => {
                      setSelectedUsers(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Usuários Alvo"
                        placeholder="Selecione usuários..."
                        margin="normal"
                        fullWidth
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option.name || "Usuário Desconhecido"}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    noOptionsText="Nenhum usuário encontrado."
                    disabled={
                      allUsers.length === 0 && !loadingUsers && !usersError
                    }
                  />
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleCloseModal} color="error" variant="outlined">
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleCreateNotification}
            disabled={loadingCreate}
          >
            {loadingCreate ? (
              <CircularProgress size={24} />
            ) : (
              "Criar Notificação"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CriarNotificacao;
